// Allowlists for sanitizeHtml. Everything not listed here is stripped by
// DOMPurify. Keep this file conservative: anything added widens the
// attack surface for HTML coming from the backend or other agents.
// See react-rewrite-plan.md §8.4 (HTML rendering is treated as untrusted).

// Tags we allow. Rationale grouped below:
//   - text structure: p, br, div, span, blockquote, pre, code
//   - emphasis:        strong, em
//   - lists:           ul, ol, li
//   - headings:        h1..h6 (assistant answers commonly include them)
//   - links:           a (href is filtered against javascript:/data:/vbscript:)
//   - tables:          table, thead, tbody, tr, td, th (markdown -> HTML tables)
//   - images:          img (src is filtered to http(s) only in sanitizeHtml.ts,
//                       mirroring entities/message/ui/MarkdownComponents.tsx's
//                       SafeImage — the html path must not be a downgrade from
//                       what markdown responses can already show)
// We deliberately do NOT allow: script, style, iframe, object, embed, svg,
// form, input, button, link, meta — see FORBID_TAGS in sanitizeHtml.ts as
// belt-and-suspenders. No inline SVG/canvas/script means no charts yet —
// that's the B-phase problem (structured chart blocks the client renders
// from model-supplied data, never model-supplied markup).
export const SAFE_TAGS: readonly string[] = [
  'p',
  'br',
  'strong',
  'em',
  'code',
  'pre',
  'ul',
  'ol',
  'li',
  'blockquote',
  'a',
  'img',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'span',
  'div',
  'table',
  'thead',
  'tbody',
  'tr',
  'td',
  'th',
];

// Attributes we allow. Rationale:
//   - href:              link targets (filtered for unsafe schemes)
//   - target, rel:       external links open safely (forced to
//                        rel="noopener noreferrer" when target="_blank" —
//                        see forceSafeRel in sanitizeHtml.ts)
//   - title:             accessible tooltips
//   - class:             styling hooks (no inline style allowed)
//   - colspan, rowspan:  table layout
//   - src, alt, loading: images (src filtered to http(s) only)
// Event handlers (on*) and `style` are intentionally excluded.
export const SAFE_ATTRS: readonly string[] = [
  'href',
  'target',
  'rel',
  'title',
  'class',
  'colspan',
  'rowspan',
  'src',
  'alt',
  'loading',
];

// SVG allowlist for the `Svg` generative-UI component (agent-authored raw
// markup — see ui/src/entities/message/ui/Svg.tsx). Separate from SAFE_TAGS
// above: markup/style tags stay excluded from HTML-format assistant text,
// but the Svg renderer needs the shape/paint primitives DOMPurify's built-in
// SVG profile supports. No <script>, <foreignObject>, or event-handler
// attributes — those remain excluded (belt-and-suspenders alongside
// FORBID_TAGS/FORBID_ATTR in sanitizeHtml.ts).
export const SAFE_SVG_TAGS: readonly string[] = [
  'svg',
  'g',
  'defs',
  'title',
  'desc',
  'path',
  'rect',
  'circle',
  'ellipse',
  'line',
  'polyline',
  'polygon',
  'text',
  'tspan',
  'use',
  'marker',
  'clipPath',
  'linearGradient',
  'radialGradient',
  'stop',
];

export const SAFE_SVG_ATTRS: readonly string[] = [
  'viewBox',
  'width',
  'height',
  'xmlns',
  'class',
  'id',
  'fill',
  'stroke',
  'stroke-width',
  'stroke-linecap',
  'stroke-linejoin',
  'stroke-dasharray',
  'opacity',
  'fill-opacity',
  'stroke-opacity',
  'transform',
  'd',
  'cx',
  'cy',
  'r',
  'rx',
  'ry',
  'x',
  'y',
  'x1',
  'y1',
  'x2',
  'y2',
  'points',
  'offset',
  'stop-color',
  'stop-opacity',
  'gradientUnits',
  'gradientTransform',
  'font-size',
  'font-family',
  'text-anchor',
  'marker-end',
  'marker-start',
];
